import React, { useState, useEffect } from "react";
import {
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Pagination,
} from "@mui/material";

import {
    Bookmark,
    Business,
    LocationOn,
    CurrencyRupee,
    BookmarkBorder,
    Work,
} from "@mui/icons-material";

import { useNavigate } from "react-router-dom";
import { api } from "../context/AuthContext";

const JOBS_PER_PAGE = 9;
function SavedJobs() {
    const navigate = useNavigate();

    const [savedJobs, setSavedJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        const totalPages = Math.ceil(savedJobs.length / JOBS_PER_PAGE);

        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        }
    }, [savedJobs, currentPage]);

    useEffect(() => {
        fetchSavedJobs();
    }, []);

    const fetchSavedJobs = async () => {
        try {
            setLoading(true);

            const response = await api.get("/jobs/saved");

            setSavedJobs(response.data.jobs || []);
        } catch (error) {
            console.error("Failed to fetch saved jobs:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveSavedJob = async (jobId) => {
        try {
            await api.delete(`/jobs/${jobId}/save`);

            setSavedJobs((prev) =>
                prev.filter((job) => job.id !== jobId)
            );
        } catch (error) {
            console.error("Failed to remove saved job:", error);
        }
    };

    const totalPages = Math.ceil(savedJobs.length / JOBS_PER_PAGE);

    const paginatedJobs = savedJobs.slice(
        (currentPage - 1) * JOBS_PER_PAGE,
        currentPage * JOBS_PER_PAGE
    );

    const startItem =
        savedJobs.length === 0
            ? 0
            : (currentPage - 1) * JOBS_PER_PAGE + 1;

    const endItem = Math.min(
        currentPage * JOBS_PER_PAGE,
        savedJobs.length
    );

    if (loading) {
        return (
            <Box
                sx={{
                    height: "70vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (savedJobs.length === 0) {
        return (
            <Container maxWidth="lg" sx={{ py: 5 }}>
                <Typography
                    variant="h4"
                    sx={{
                        color: "#1E3A8A",
                        fontWeight: "bold",
                        fontSize: { xs: "1.5rem", md: "2rem" },
                    }}
                >
                    Saved Jobs
                </Typography>

                <Typography
                    color="text.secondary"
                    sx={{ mb: 5 }}
                >
                    Keep track of your bookmarked opportunities.
                </Typography>

                <Card
                    elevation={0}
                    sx={{
                        border: "1px solid #E5E7EB",
                        borderRadius: "16px",
                        textAlign: "center",
                        py: 8,
                    }}
                >
                    <Bookmark
                        sx={{
                            fontSize: 70,
                            color: "#CBD5E1",
                            mb: 2,
                        }}
                    />

                    <Typography
                        variant="h6"
                        fontWeight="bold"
                    >
                        No Saved Jobs Yet
                    </Typography>

                    <Typography
                        color="text.secondary"
                        sx={{
                            mt: 1,
                            mb: 3,
                        }}
                    >
                        Jobs you bookmark will appear here.
                    </Typography>

                    <Button
                        variant="contained"
                        onClick={() => navigate("/jobs")}
                        sx={{
                            bgcolor: "#1E3A8A",
                            textTransform: "none",
                            borderRadius: "8px",
                        }}
                    >
                        Browse Jobs
                    </Button>
                </Card>
            </Container>
        );
    }

    return (
        <Container
            maxWidth="xl"
            sx={{
                py: 4,
            }}
        >
            <Box sx={{ mb: 4 }}>
                <Typography
                    variant="h4"
                    sx={{
                        color: "#1E3A8A",
                        fontWeight: "bold",
                        fontSize: { xs: "1.5rem", md: "2rem" },
                    }}
                >
                    Saved Jobs
                </Typography>

                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                >
                    Keep track of your bookmarked opportunities.
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {paginatedJobs.map((job) => (
                    <Grid item xs={12} md={6} lg={4} key={job.id}>
                        <Card
                            sx={{
                                borderRadius: "16px",
                                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                border: "1px solid #eef2f6",
                                position: "relative",
                                overflow: "hidden",
                                "&:hover": {
                                    transform: "translateY(-8px)",
                                    boxShadow: "0 12px 30px rgba(0, 139, 139, 0.12)",
                                    borderColor: "primary.light",
                                },
                            }}
                        >
                            <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        mb: 1,
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRemoveSavedJob(job.id)}
                                    >
                                        <Bookmark
                                            sx={{
                                                fontSize: 22,
                                                color: "#424242",
                                            }}
                                        />
                                    </IconButton>
                                </Box>

                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: "800",
                                        color: "text.primary",
                                        lineHeight: 1.2,
                                        mb: 0.5,
                                        fontSize: "1.1rem",
                                    }}
                                >
                                    {job.title}
                                </Typography>

                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        mb: 2,
                                        color: "primary.main",
                                        fontWeight: 700,
                                        letterSpacing: "0.2px",
                                    }}
                                >
                                    {job.company_name}
                                </Typography>

                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1.2,
                                        mb: 3,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        <LocationOn
                                            sx={{
                                                fontSize: 18,
                                                color: "primary.light",
                                            }}
                                        />

                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontWeight: 500 }}
                                        >
                                            {job.location || "Remote"}
                                        </Typography>
                                    </Box>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        <Business
                                            sx={{
                                                fontSize: 18,
                                                color: "primary.light",
                                            }}
                                        />

                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontWeight: 500 }}
                                        >
                                            {(() => {
                                                const mode =
                                                    job.work_mode === "office"
                                                        ? "In Office"
                                                        : job.work_mode || "Full Time";

                                                return mode
                                                    .split(" ")
                                                    .map(
                                                        (word) =>
                                                            word.charAt(0).toUpperCase() +
                                                            word.slice(1).toLowerCase()
                                                    )
                                                    .join(" ");
                                            })()}{" "}
                                            • {job.experience_range || "0-1"} Yrs
                                        </Typography>
                                    </Box>

                                    {job.salary && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                            }}
                                        >
                                            <CurrencyRupee
                                                sx={{
                                                    fontSize: 18,
                                                    color: "secondary.main",
                                                }}
                                            />

                                            <Typography
                                                variant="body2"
                                                color="text.primary"
                                                fontWeight="700"
                                            >
                                                {job.salary}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 1,
                                        flexWrap: "wrap",
                                        mt: "auto",
                                    }}
                                >
                                    {job.work_mode && (
                                        <Chip
                                            label={
                                                job.work_mode === "office"
                                                    ? "Office"
                                                    : job.work_mode
                                            }
                                            size="small"
                                            sx={{
                                                fontSize: "11px",
                                                height: "24px",
                                                bgcolor:
                                                    job.work_mode === "remote"
                                                        ? "#e8f5e9"
                                                        : job.work_mode === "hybrid"
                                                            ? "#fff3e0"
                                                            : "#e3f2fd",
                                                color:
                                                    job.work_mode === "remote"
                                                        ? "#2e7d32"
                                                        : job.work_mode === "hybrid"
                                                            ? "#e65100"
                                                            : "#1565c0",
                                                fontWeight: 700,
                                                textTransform: "uppercase",
                                                borderRadius: "6px",
                                            }}
                                        />
                                    )}

                                    {job.experience_range && (
                                        <Chip
                                            label={`${job.experience_range} YRS`}
                                            size="small"
                                            sx={{
                                                fontSize: "11px",
                                                height: "24px",
                                                bgcolor: "#f0f4f8",
                                                color: "#475467",
                                                fontWeight: 700,
                                                borderRadius: "6px",
                                            }}
                                        />
                                    )}
                                </Box>
                            </CardContent>

                            <Box sx={{ p: 2, pt: 0 }}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    disableElevation
                                    endIcon={<span>→</span>}
                                    sx={{
                                        borderRadius: "10px",
                                        fontWeight: "700",
                                        py: 1.2,
                                        fontSize: "0.9rem",
                                        bgcolor: "primary.main",
                                        transition: "all 0.2s",
                                        "&:hover": {
                                            bgcolor: "primary.dark",
                                            gap: 1,
                                        },
                                    }}
                                    onClick={() => navigate(`/jobs/${job.id}`)}
                                >
                                    View Details
                                </Button>
                            </Box>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            <Box
                sx={{
                    mt: 5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                }}
            >
                <Typography
                    variant="body2"
                    color="text.secondary"
                >
                    Showing {startItem} to {endItem} of {savedJobs.length} saved jobs
                </Typography>

                {totalPages > 1 && (
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(event, value) => {
                            setCurrentPage(value);

                            window.scrollTo({
                                top: 0,
                                behavior: "smooth",
                            });
                        }}
                        color="primary"
                        size="large"
                        sx={{
                            "& .MuiPaginationItem-root": {
                                fontWeight: 700,
                                borderRadius: "8px",

                                "&.Mui-selected": {
                                    boxShadow:
                                        "0 4px 10px rgba(0,139,139,0.3)",
                                },
                            },
                        }}
                    />
                )}
            </Box>
        </Container>
    );
}

export default SavedJobs;